import { type ReactNode } from 'react';
import { IconButton } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
import { useViewport } from '@/hooks';
import { XIcon } from '@/icons';
import { DialogBody, DialogHeader, ResponsiveDialog } from '@/ui_v3';

/**
 * 피그마 .PlaySharePopover 데스크탑(400×610): 플레이 헤더 「공유」 아래에 붙는 창.
 * Radix Dialog 밖이라 DialogHeader(DialogTitle) 를 쓸 수 없어 같은 모양을 직접 그린다.
 */
function SharePopoverFrame({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const {
    t
  } = useTranslation(['common']);
  return <div className="flex h-[610px] max-h-[calc(100vh-80px)] w-[400px] flex-col overflow-hidden rounded-lg bg-default shadow-xl" data-sentry-component="SharePopoverFrame" data-sentry-source-file="ShareModalFrame.tsx">
      <div className="flex items-center px-md pb-xs pt-lg">
        <h2 className="text-heading-md-bold">{title}</h2>
        <IconButton className="ml-auto hover:bg-transparent" size="sm" variant="transparent" intent="neutral" aria-label={t('global.close.button')} onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="ShareModalFrame.tsx">
          <XIcon size="md" data-sentry-element="XIcon" data-sentry-source-file="ShareModalFrame.tsx" />
        </IconButton>
      </div>
      <div className="flex min-h-0 flex-1 flex-col items-center gap-md overflow-auto px-md pb-lg text-body-md-regular">
        {children}
      </div>
    </div>;
}

/**
 * 공유 창의 겉모양. 내용(ShareContent 등)은 같고 어디에 띄우느냐만 다르다.
 * - dialog: 가운데 창, 모바일은 핸들이 있는 하단 시트(끌어내려 닫기)
 * - popover: 플레이 헤더 팝오버 — 위치·열림은 호출부의 Popover 가 맡는다
 */
export function ShareModalFrame({
  presentation = 'dialog',
  title,
  disableOpenAnimation,
  onClose,
  children
}: {
  presentation?: 'dialog' | 'popover';
  title: string;
  disableOpenAnimation?: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const {
    isMobile
  } = useViewport();
  if (presentation === 'popover') {
    return <SharePopoverFrame title={title} onClose={onClose}>
        {children}
      </SharePopoverFrame>;
  }
  return <ResponsiveDialog size={isMobile ? 'lg' : 'md'} disableOpenAnimation={disableOpenAnimation}
  // 피그마 PlaySharePopover 모바일: 핸들이 있는 하단 시트, 끌어내려 닫는다.
  hasDrawerHandle={true} isSwipeDismissible={true}
  // 높이는 내용에 맞춘다. 610 고정은 플레이 팝오버(위 SharePopoverFrame)만 — 이 창은
  // 보드·학급·폴더 공유처럼 내용이 적은 곳도 쓰므로 고정하면 빈 공간이 남는다.
  onClose={onClose} data-sentry-element="ResponsiveDialog" data-sentry-component="ShareModalFrame" data-sentry-source-file="ShareModalFrame.tsx">
      <DialogHeader title={title} hasCloseButton={true} onClose={onClose} data-sentry-element="DialogHeader" data-sentry-source-file="ShareModalFrame.tsx" />
      <DialogBody className="flex min-h-0 flex-1 flex-col items-center gap-md overflow-auto px-md pb-lg mobile-lg:px-lg" data-sentry-element="DialogBody" data-sentry-source-file="ShareModalFrame.tsx">
        {children}
      </DialogBody>
    </ResponsiveDialog>;
}